import React, { useState } from "react";
import { useRecordContext, useTranslate } from "react-admin";
import { useFormContext } from "react-hook-form";
import { HttpError } from "ra-core";

import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

import { useFileUpload } from "../hooks/useFileUpload";
import { ACCEPTS, formatFileSize } from "../utils/file";
import { getApiBase } from "../utils/api";
import { FileNameExtensionError, FileSizeError, ImageProcessingError } from "../utils/error";

const commonStyles = {
  hidden: {
    display: "none",
  },
  link: {
    textDecorationSkipInk: "none",
  },
  tooltip: {
    "&:hover": {
      cursor: "pointer",
    },
  },
  tooltipText: {
    fontSize: 14,
    lineHeight: 1.3,
  },
};

function isArrayInput(source: string) {
  const re = /(\w+)\.(\d+)/;
  return !!source.match(re);
}

function getItem(values: any, source: string) {
  const re = /(\w+)\.(\d+)/;
  const result = source.match(re);
  if (result === null) {
    throw new Error("invalid source name");
  }
  const property = result[1];
  const index = parseInt(result[2]);
  const items = values[property];
  return items?.[index];
}

const formatFileName = (file_name: string, file_size: number): string => {
  const [size, unit] = formatFileSize(file_size);
  return `${file_name} (${size}${unit})`;
};

const FileLinkText = (props: { href: string; text: string }) => {
  const { href, text } = props;

  return (
    <Link href={href} target="_blank" variant="body2" sx={commonStyles.link}>
      {text}
    </Link>
  );
};

const UploadFileButton = (props: { onFileSelected: (file: File) => void }) => {
  const { onFileSelected } = props;

  const onChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const { files } = event.currentTarget;
    if (!files || files.length === 0) {
      window.alert("ファイルを選択してください");
      return null;
    } else if (files.length > 1) {
      window.alert("ファイルは複数同時に選択できません");
      return null;
    }
    const file = files[0];
    onFileSelected(file);
  };

  return (
    <Box display="flex">
      <Box>
        <label>
          <input type="file" accept={ACCEPTS} style={commonStyles.hidden} onChange={onChange} />
          <Button variant="contained" component="span" startIcon={<CloudUploadIcon />}>
            ファイル選択
          </Button>
        </label>
      </Box>
      <Box ml={2} py={"6px"}>
        <Tooltip
          enterTouchDelay={0}
          sx={commonStyles.tooltip}
          title={
            <Box sx={commonStyles.tooltipText}>
              <p>画像（.jpg, .jpeg, .png）、PDF（.pdf）、Zipファイル（.zip）がアップロード可能です。</p>
              <p>アップロードできるファイルサイズの上限は100MBまでです。</p>
            </Box>
          }
        >
          <HelpOutlineIcon />
        </Tooltip>
      </Box>
    </Box>
  );
};

const UploadedFileLink = (props: { href: string; fileName: string; fileSize: number }) => {
  const { fileName, fileSize, href } = props;
  const text = formatFileName(fileName, fileSize);
  return <FileLinkText href={href} text={text} />;
};

export const AttachmentFileField = (props: {
  source?: string;
  label?: string | boolean | undefined;
  styles?: Record<string, any>;
}) => {
  const record = useRecordContext();
  const { source, styles } = props;
  if (!record) {
    return null;
  }
  const keys = Object.keys(record);
  const inArray = ["uuid", "file_name", "file_size_in_bytes"].every((key) => keys.includes(key));

  let uuid: string, file_name: string, file_size_in_bytes: number;
  if (inArray) {
    uuid = record.uuid;
    file_name = record.file_name;
    file_size_in_bytes = record.file_size_in_bytes;
  } else {
    if (!source || !record[source]) {
      return null;
    }
    uuid = record[source].uuid;
    file_name = record[source].file_name;
    file_size_in_bytes = record[source].file_size_in_bytes;
  }

  const apiUrl = getApiBase() + `/attachments?uuid=${uuid}`;

  const styleProps: any = styles || {
    my: 2,
  };

  return (
    <Box {...styleProps}>
      <UploadedFileLink href={apiUrl} fileName={file_name} fileSize={file_size_in_bytes} />
    </Box>
  );
};

const createErrorMessage = (error: Error, translate: (key: string) => string) => {
  if (error instanceof FileSizeError) {
    return translate("ui.error.file_size_is_too_big");
  } else if (error instanceof FileNameExtensionError) {
    return translate("ui.error.filename_extension_is_not_allowed");
  } else if (error instanceof ImageProcessingError) {
    return translate("ui.error.image_processing_is_failed");
  } else if (error instanceof HttpError) {
    return translate("ui.error.upload_is_failed");
  }
  return translate("ui.error.unknown_error");
};

export const AttachmentFileInput = (props: {
  basePath?: string | undefined;
  source?: string;
  fullWidth?: boolean;
  label?: string | boolean | undefined;
}) => {
  const { source } = props;
  // HACK! propsにsourceを渡していないケースでは、react-adminが暗黙的にsourceを設定している。
  // そのため、コンポーネント内部では必ずsourceに値がある状態になる。
  const _source = source as string;

  const [file, setFile] = useState<File | null>(null);
  const [uploading, upload, error] = useFileUpload();
  const translate = useTranslate();
  const { getValues, setValue } = useFormContext();

  if (!source) {
    return null;
  }

  let target;
  if (isArrayInput(source)) {
    target = getItem(getValues(), _source);
  } else {
    target = getValues(_source);
  }
  const uuid = target?.uuid;
  const file_name = target?.file_name;
  const file_size_in_bytes = target?.file_size_in_bytes;

  const onClickRemove = () => {
    setValue(_source, null, { shouldDirty: true, shouldValidate: true });
    setFile(null);
  };

  const updateField = (newUuid: string, fileName: string, fileSize: number) => {
    setValue(`${_source}.uuid`, newUuid, { shouldDirty: true, shouldValidate: true });
    setValue(`${_source}.file_name`, fileName, { shouldDirty: true, shouldValidate: true });
    setValue(`${_source}.file_size_in_bytes`, fileSize, { shouldDirty: true, shouldValidate: true });
  };

  // アップロード済みのファイルがある場合
  if (uuid) {
    const apiUrl = getApiBase() + `/attachments?uuid=${uuid}`;
    return (
      <Box my={2} display="flex" alignItems="center">
        <Box flexGrow={9999}>
          <UploadedFileLink href={apiUrl} fileName={file_name} fileSize={file_size_in_bytes} />
        </Box>
        {!isArrayInput(source) && (
          <Box>
            <Button onClick={onClickRemove}>
              <RemoveCircleOutlineIcon />
              <Box ml={1} display="inline">
                {translate("ra.action.delete")}
              </Box>
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  if (uploading) {
    return (
      <Box my={2}>
        <Box width={200} height={12} pt={1}>
          <LinearProgress />
        </Box>
      </Box>
    );
  }

  if (file !== null) {
    const blob = new Blob([file.slice(0)], { type: file.type });
    const blobUrl = URL.createObjectURL(blob);
    return (
      <Box my={2}>
        <UploadedFileLink href={blobUrl} fileName={file.name} fileSize={file.size} />
      </Box>
    );
  }

  const onFileSelected = async (file: File) => {
    const newUuid = await upload(file);
    if (newUuid !== null) {
      updateField(newUuid, file.name, file.size);
      setFile(file);
    }
  };

  return (
    <Box my={2}>
      <UploadFileButton onFileSelected={onFileSelected} />
      {error && (
        <Box mt={1}>
          <Typography color="error" variant="caption">
            {createErrorMessage(error, translate)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
